import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { AuthService, AuthResult } from '../../shared/services/auth/auth.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { ToastService } from '../../shared/services/toast/toast.service';
import { Botmaa } from '../../shared/components/botmaa/botmaa';
import { FormField } from '../../shared/components/form-field/form-field';
import { PasswordInput } from '../../shared/components/password-input/password-input';
import { FormSubmitButton } from '../../shared/components/form-submit-button/form-submit-button';
import { Divider } from '../../shared/components/divider/divider';
import { AuthLink } from '../../shared/components/auth-link/auth-link';
import { GoogleOAuthService } from '../../shared/services/google-oauth/google-oauth.service';
import { UserProfileService } from '../../shared/services/user-profile/user-profile.service';
import { NotificationService } from '../../core/firebase/notification.service';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    RouterModule,
    Botmaa,
    FormField,
    PasswordInput,
    FormSubmitButton,
    Divider,
    AuthLink,
  ],
  selector: 'app-login',
  templateUrl: './login.html',
  styleUrls: ['./login.scss'],
})
export class Login implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('emailField', { static: false }) emailField?: ElementRef<HTMLElement>;
  @ViewChild('passwordField', { static: false }) passwordField?: ElementRef<HTMLElement>;
  @ViewChild('rememberMeCheckbox', { static: false }) rememberMeCheckbox?: ElementRef<HTMLInputElement>;

  loginForm!: FormGroup;

  private readonly REMEMBERED_EMAIL_KEY = 'rememberedEmail';

  loading = false;
  googleLoading = false;
  error: string | null = null;
  rememberMeChecked = false; // Property to track checkbox state
  showResendVerification = false; // Show resend verification link
  resendingVerification = false; // Loading state for resend verification
  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
    private googleOAuthService: GoogleOAuthService,
    private toastService: ToastService,
    private userProfileService: UserProfileService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    // Initialize form with remembered email if available
    this.initializeForm();

    // Subscribe to form value changes after form is initialized
    this.loginForm.valueChanges.subscribe(() => {
      this.error = null;
    });

    // Only check authentication in browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Use setTimeout to ensure localStorage is fully accessible
      // This prevents race conditions during page initialization
      setTimeout(() => {
        if (this.authService.isAuthenticated()) {
          // Only redirect if we're actually on the login page
          if (this.router.url === '/login' || this.router.url.startsWith('/login')) {
            this.router.navigate(['/dashboard'], { replaceUrl: true });
            return;
          }
        }
      }, 0);
    }

    // Check for OAuth errors in query params
    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        if (params['error']) {
          this.error = params['message'] || 'Authentication failed. Please try again.';
        }
      }),
    );

    // Initialize Google OAuth
    this.subscriptions.add(
      this.googleOAuthService.isReady().subscribe((ready) => {
        if (!ready) {
          console.warn('Google OAuth service is not ready. Check your configuration.');
        }
      }),
    );
  }

  ngAfterViewInit() {
    // Only run in browser environment (not during SSR)
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Ensure checkbox is properly checked after view init
    setTimeout(() => {
      this.ensureCheckboxState();
    }, 150);

    // Prevent password field from being focused and focus email field instead
    setTimeout(() => {
      // Use Angular's ElementRef to blur password field (blur() is safe even if not focused)
      if (this.passwordField?.nativeElement) {
        const passwordNativeElement = this.passwordField.nativeElement;
        const inputElement = passwordNativeElement.querySelector('input') as HTMLInputElement;
        if (inputElement) {
          inputElement.blur();
        }
        passwordNativeElement.blur();
      }

      // Use Angular's ElementRef to focus email field (only if empty)
      if (this.emailField?.nativeElement) {
        const emailNativeElement = this.emailField.nativeElement;
        const emailInput = emailNativeElement.querySelector('input') as HTMLInputElement;
        if (emailInput && !emailInput.value) {
          emailInput.focus();
        }
      }
    }, 100); // Slightly longer delay to ensure PrimeNG has initialized
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  get emailInvalid(): boolean {
    const control = this.loginForm.get('email');
    return !!(control?.touched && control.invalid);
  }

  get passwordInvalid(): boolean {
    const control = this.loginForm.get('password');
    return !!(control?.touched && control.invalid);
  }

  /**
   * Validates email/username/mobile number field
   * Checks for empty, whitespace-only, and minimum length
   */
  private validateEmailField(value: string | null | undefined): boolean {
    if (value === null || value === undefined) return false;
    const trimmed = value.trim();
    return trimmed.length > 0;
  }

  /**
   * Validates password field
   * Checks for empty, whitespace-only, and minimum length
   */
  private validatePasswordField(value: string | null | undefined): boolean {
    if (value === null || value === undefined) return false;
    const trimmed = value.trim();
    return trimmed.length > 0;
  }

  handleSubmit(): void {
    // Mark all fields as touched to show validation errors
    this.loginForm.markAllAsTouched();

    // Get original form values
    const emailOriginal = this.loginForm.get('email')?.value;
    const passwordOriginal = this.loginForm.get('password')?.value;

    // Trim whitespace from values
    const emailValue = typeof emailOriginal === 'string' ? emailOriginal.trim() : '';
    // Never trim passwords; whitespace can be part of a valid credential.
    const passwordValue = typeof passwordOriginal === 'string' ? passwordOriginal : '';

    // Update form controls with trimmed values and re-validate
    this.loginForm.patchValue({
      email: emailValue,
      password: passwordValue,
    });

    // Trigger validation on all controls
    this.loginForm.get('email')?.updateValueAndValidity({ emitEvent: false });
    this.loginForm.get('password')?.updateValueAndValidity({ emitEvent: false });

    // Meticulous validation - check both form validation and custom validation
    const isEmailValid = this.validateEmailField(emailValue) && this.loginForm.get('email')?.valid;
    const isPasswordValid =
      this.validatePasswordField(passwordValue) && this.loginForm.get('password')?.valid;

    // Set custom errors if validation fails
    if (!isEmailValid) {
      const emailControl = this.loginForm.get('email');
      if (emailControl) {
        emailControl.setErrors({ required: true, ...emailControl.errors });
        emailControl.markAsTouched();
      }
    }

    if (!isPasswordValid) {
      const passwordControl = this.loginForm.get('password');
      if (passwordControl) {
        passwordControl.setErrors({ required: true, ...passwordControl.errors });
        passwordControl.markAsTouched();
      }
    }

    // If any validation fails, stop submission (messages align with API 422 responses)
    if (!isEmailValid || !isPasswordValid) {
      if (isPlatformBrowser(this.platformId)) {
        let errorMessage = 'Please fill in all required fields correctly.';
        if (!isEmailValid && !isPasswordValid) {
          errorMessage = 'Email,Password, fields should not be empty.';
        } else if (!isPasswordValid) {
          errorMessage = 'Password cannot be empty';
        } else if (!isEmailValid) {
          errorMessage = 'Email cannot be empty';
        }
        this.toastService.error(errorMessage, 'Validation Error');
      }
      return;
    }

    // All validations passed, proceed with login
    this.loading = true;
    this.error = null;

    // Get remember me value
    const rememberMe = this.loginForm.get('rememberMe')?.value ?? false;

    this.subscriptions.add(
      this.authService.login(emailValue, passwordValue).subscribe({
        next: (result: AuthResult) => {
          this.loading = false;
          this.showResendVerification = false; // Reset resend link visibility
          if (result.success) {
            // Store email if remember me is checked, otherwise clear it
            // Only store after successful login
            this.handleRememberEmail(emailValue, rememberMe);

            // Refresh user profile, then register FCM token for push notifications
            this.userProfileService.fetchUserProfile(true).subscribe({
              next: (profile) => {
                if (profile?.id) {
                  void this.notificationService.registerTokenAfterLogin(profile.id);
                }
              },
            });

            // Navigate after successful login
            // Check for returnUrl in query params, otherwise go to dashboard
            // Toast notification is already shown by AuthService
            setTimeout(() => {
              // Get returnUrl from current route snapshot
              const returnUrl = this.route.snapshot.queryParams['returnUrl'];
              if (returnUrl && typeof returnUrl === 'string' && returnUrl.startsWith('/')) {
                // Navigate to the returnUrl if it's a valid internal route
                this.router.navigate([returnUrl], { replaceUrl: true }).catch((err) => {
                  console.error('Navigation error:', err);
                  // Fallback to dashboard if returnUrl is invalid
                  this.router.navigate(['/dashboard'], { replaceUrl: true });
                });
              } else {
                // No returnUrl, go to dashboard
                this.router.navigate(['/dashboard'], { replaceUrl: true }).catch((err) => {
                  console.error('Navigation error:', err);
                  // Fallback navigation
                  this.router.navigate(['/'], { replaceUrl: true });
                });
              }
            }, 100);
          } else {
            // Error message is already shown via toaster in AuthService
            this.error = result.error || 'Login failed. Please try again.';
            // Check if error is related to unverified email
            this.checkIfUnverifiedEmail(result.error, emailValue);
          }
        },
        error: (err) => {
          // Don't log full error as it might contain sensitive data
          if (isPlatformBrowser(this.platformId)) {
            console.error('Login error:', {
              status: err?.status,
              message: err?.message || err?.error?.detail || 'Login failed',
            });
          }
          this.loading = false;
          // Error toast is already shown by AuthService
          const errorMessage = err?.message || err?.error?.detail || err?.error?.message || 'Something went wrong. Please try again.';
          this.error = errorMessage;
          // Check if error is related to unverified email
          this.checkIfUnverifiedEmail(errorMessage, emailValue);
        },
      }),
    );
  }

  /**
   * Handle Google OAuth login
   */
  handleGoogleLogin(): void {
    this.googleLoading = true;
    this.error = null;

    try {
      this.googleOAuthService.loginWithGoogle();
      // Note: User will be redirected to Google, so we don't need to handle response here
    } catch (error: any) {
      this.googleLoading = false;
      this.error = 'Failed to initiate Google login. Please try again.';
      console.error('Google login error:', error);
    }
  }

  /**
   * Initialize form with remembered email if available
   */
  private initializeForm(): void {
    let initialEmail = '';
    let initialRememberMe = false;

    if (isPlatformBrowser(this.platformId)) {
      try {
        const rememberedEmail = localStorage.getItem(this.REMEMBERED_EMAIL_KEY);
        if (rememberedEmail && rememberedEmail.trim()) {
          initialEmail = rememberedEmail.trim();
          initialRememberMe = true;
        }
      } catch (error) {
        // Silently fail if localStorage is not accessible
        console.warn('Could not load remembered email:', error);
      }
    }

    // Initialize form with remembered values
    this.loginForm = new FormGroup({
      email: new FormControl(initialEmail, [Validators.required]),
      password: new FormControl('', [Validators.required]),
      rememberMe: new FormControl(initialRememberMe),
    });

    // Sync the component property with form control value
    this.rememberMeChecked = initialRememberMe;
  }

  /**
   * Ensure checkbox state matches form control value after view initialization
   */
  private ensureCheckboxState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const rememberMeControl = this.loginForm.get('rememberMe');
    if (rememberMeControl) {
      // Sync component property with form control value
      this.rememberMeChecked = rememberMeControl.value === true;

      // Use Angular's ElementRef to access checkbox element
      setTimeout(() => {
        if (this.rememberMeCheckbox?.nativeElement) {
          this.rememberMeCheckbox.nativeElement.checked = this.rememberMeChecked;
        }
        // Trigger change detection to ensure the view reflects the form control value
        this.cdr.detectChanges();
      }, 0);
    }
  }

  /**
   * Handle remember me checkbox change
   */
  onRememberMeChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const checked = checkbox.checked;

    // Update component property
    this.rememberMeChecked = checked;

    // Update form control
    const rememberMeControl = this.loginForm.get('rememberMe');
    if (rememberMeControl) {
      rememberMeControl.setValue(checked, { emitEvent: true });
    }

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Store or clear email based on rememberMe checkbox
   * Only stores email, never stores password for security
   */
  private handleRememberEmail(email: string, rememberMe: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      if (rememberMe && email) {
        // Store email in localStorage
        localStorage.setItem(this.REMEMBERED_EMAIL_KEY, email);
      } else {
        // Clear stored email if rememberMe is unchecked
        localStorage.removeItem(this.REMEMBERED_EMAIL_KEY);
      }
    } catch (error) {
      // Silently fail if localStorage is not accessible
      console.warn('Could not save remembered email:', error);
    }
  }

  /**
   * Check if the error is related to unverified email
   */
  private checkIfUnverifiedEmail(errorMessage: string | null | undefined, email: string): void {
    if (!errorMessage || !email) {
      this.showResendVerification = false;
      return;
    }

    const errorLower = errorMessage.toLowerCase();
    const isUnverifiedError =
      errorLower.includes('verify') ||
      errorLower.includes('verification') ||
      errorLower.includes('not verified') ||
      errorLower.includes('email not verified') ||
      errorLower.includes('please verify');

    this.showResendVerification = isUnverifiedError;
  }

  /**
   * Handle resend verification email
   */
  handleResendVerification(): void {
    const emailValue = this.loginForm.get('email')?.value?.trim();
    if (!emailValue) {
      this.toastService.error('Please enter your email address first.', 'Error');
      return;
    }

    this.resendingVerification = true;
    this.subscriptions.add(
      this.authService.resendVerificationEmail(emailValue).subscribe({
        next: (result) => {
          this.resendingVerification = false;
          if (result.success) {
            // Success toast is already shown by AuthService
            this.showResendVerification = false; // Hide the link after successful send
          }
        },
        error: (err) => {
          this.resendingVerification = false;
          // Error toast is already shown by AuthService
        },
      }),
    );
  }
}
